// Intro overlay (design §4.1/§4.2): one line of copy, one instruction, and the
// tap that both starts the run and unlocks audio.

import { t } from '../i18n';
import type { EntryType } from '../types';
import { el, enter } from './dom';

export interface IntroOptions {
  entryType: EntryType;
  opponentName: string;
  opponentScoreM: number;
  /** Opponent is the official mascot — 「前一位路人」 would be a lie (journey review). */
  opponentOfficial: boolean;
  /** Device's first ever run — show the slide hint (design §2.3). */
  showSlideHint: boolean;
  /** Linked run was flagged; the ghost got swapped for the official one. */
  flagged: boolean;
  /**
   * §4bis.28: this link opens on a duel slice. Present ⇒ the screen offers two
   * explicit choices instead of tap-anywhere — 应战 · 12 秒 as the primary and
   * 跑全程 as the secondary. Absent ⇒ the shipped screen, byte-for-byte, which
   * is what `DUEL_PCT=0` produces for everyone.
   */
  duel?: { sourceEndM: number };
  /** §4bis.31: present iff this link carried the 帮我 verb AND the server
   *  minted a hand-back slice. Changes the words on the duel entry screen,
   *  never the routing. */
  handoff?: { fromName: string; frontierM: number; startM: number; toIsYou: boolean };
}

/** What the player chose. `full` is the only answer a non-duel screen gives. */
export type IntroChoice = 'duel' | 'full';

const LINE_KEY = {
  link: 'intro.link',
  first: 'intro.first',
  walkin: 'intro.walkin',
  rerun: 'intro.rerun',
} as const;

/** Resolves on the first tap / space / enter, or on a duel screen's button. */
export function showIntro(root: HTMLElement, opts: IntroOptions): Promise<IntroChoice> {
  if (opts.duel) return duelIntroScreen(root, opts, opts.duel, opts.handoff);
  return new Promise((resolve) => {
    // A cold-start walk-in faces the mascot, not a passer-by — use the plain
    // 「{name} 跑了 {n} 米」 shape instead of claiming 前一位路人.
    const lineKey =
      opts.entryType === 'walkin' && opts.opponentOfficial ? 'intro.first' : LINE_KEY[opts.entryType];
    const line = t(lineKey, {
      name: opts.opponentName,
      n: opts.opponentScoreM,
    });

    const screen = el('div', { class: 'screen screen--intro', attrs: { role: 'button' } }, [
      opts.flagged ? el('div', { class: 'pill pill--warn', text: t('intro.flagged') }) : null,
      el('div', { class: 'intro-line', text: line }),
      // Cold-read finding: the instruction used to live inside the headline and
      // got parsed as one weird sentence. Score line above, how-to pills below.
      el('div', { class: 'intro-hints' }, [
        el('span', { class: 'pill', text: t('intro.hintJump') }),
        opts.showSlideHint ? el('span', { class: 'pill', text: t('intro.hintSlide') }) : null,
        // "Which runner am I?" was the panel's single biggest unknown; link
        // entries additionally never said the sender's GHOST races beside you
        // (journey review finding).
        opts.entryType === 'link'
          ? el('span', { class: 'pill', text: t('intro.hintGhost') })
          : opts.entryType !== 'rerun'
            ? el('span', { class: 'pill', text: t('intro.hintLane') })
            : null,
      ]),
      el('div', { class: 'intro-tap', text: t('intro.tap') }),
    ]);

    const done = (e: Event): void => {
      if (e instanceof KeyboardEvent && e.key !== ' ' && e.key !== 'Enter') return;
      // Consume the start gesture: without this the same pointerdown bubbles to
      // the stage listener after input enables and records a frame-0 jump,
      // making a real run differ byte-wise from its replay (journey finding).
      e.stopPropagation();
      window.removeEventListener('keydown', done);
      screen.remove();
      resolve('full');
    };

    screen.addEventListener('pointerdown', done);
    window.addEventListener('keydown', done);
    root.append(screen);
    enter(screen);
  });
}

/**
 * The duel entry screen (§4bis.28). Two explicit buttons rather than
 * tap-anywhere, because there are now two different runs behind this link and
 * a stray tap must not pick one of them for you.
 *
 * The pre-declared severance if this splits playrate instead of growing it
 * (design §Risks): collapse to duel-only with 跑全程 behind a text link — which
 * is a change to this function and nothing else.
 */
function duelIntroScreen(
  root: HTMLElement,
  opts: IntroOptions,
  duel: { sourceEndM: number },
  /**
   * §4bis.31: this link was a 帮我 hand-back, so the SAME two-button screen
   * asks the opposite question.
   *
   * The routing is deliberately unchanged (§4bis.31 §4): a hand-back is still a
   * `link` entry and still lands here, because the choice between "take this
   * life" and "run the whole track" is exactly the choice a challenge offers,
   * and a run mints only if the player picks the slice. What changes is the
   * sentence and the verb on the primary button — 接住, not 应战.
   *
   * Without this the recipient read 「{name} 在 {d} 米倒下」 over 「先活过他
   * 倒下的地方」: *beat the person who just trusted you*, on the one entry the
   * whole verb exists to create. T1 measures whether the ADDRESSED person comes
   * back, so this screen is where the addressing has to be visible.
   */
  handoff?: {
    fromName: string;
    frontierM: number;
    startM: number;
    toIsYou: boolean;
  },
): Promise<IntroChoice> {
  return new Promise((resolve) => {
    const finish = (choice: IntroChoice) => (e: Event): void => {
      // Same reason the tap-anywhere path consumes its gesture: without this
      // the pointerdown bubbles to the stage listener once input enables and
      // records a frame-S jump the player never made.
      e.stopPropagation();
      screen.remove();
      resolve(choice);
    };

    const primary = el('button', {
      class: 'btn btn--primary',
      text: handoff
        ? handoff.toIsYou
          ? t('handoff.openCta')
          : t('handoff.openOtherCta')
        : t('duel.cta'),
      attrs: { type: 'button' },
    });
    primary.addEventListener('click', finish('duel'));

    const secondary = el('button', {
      class: 'btn btn--ghost',
      text: t('duel.full'),
      attrs: { type: 'button' },
    });
    secondary.addEventListener('click', finish('full'));

    const screen = el('div', { class: 'screen screen--intro screen--intro-duel' }, [
      el('div', {
        class: 'intro-line',
        text: handoff
          ? handoff.toIsYou
            ? t('handoff.open', { name: handoff.fromName })
            : t('handoff.openOther', { p: handoff.frontierM })
          : t('duel.banner', { name: opts.opponentName, d: duel.sourceEndM }),
      }),
      el('div', {
        class: 'intro-sub',
        text: handoff
          ? t('handoff.openSub', { p: handoff.frontierM, s: handoff.startM })
          : t('duel.aim'),
      }),
      el('div', { class: 'intro-hints' }, [
        el('span', { class: 'pill', text: t('intro.hintJump') }),
        opts.showSlideHint ? el('span', { class: 'pill', text: t('intro.hintSlide') }) : null,
        el('span', { class: 'pill', text: t('intro.hintGhost') }),
      ]),
      el('div', { class: 'result-actions' }, [primary, secondary]),
      // §4bis.30: the promise, made honest. 12 s is what the ask BUYS — the
      // distance to his grave — not how long the run lasts. Saying only「12 秒」
      // under a run that carries on to a real death would be a lie the player
      // discovers by playing.
      el('div', {
        class: 'intro-note',
        // `handoff.ctaSub` belongs to the SHARER's death screen ("he picks it
        // up where YOU fell"); shown here it addresses the reader as somebody
        // who has fallen, and the reader has not run a step yet.
        text: handoff ? t('handoff.openNote') : t('duel.ctaSub'),
      }),
    ]);

    root.append(screen);
    enter(screen);
  });
}
